import React from "react"

import { useBackToClose } from "@/lib/use-back-to-close"
import { cn } from "@/lib/utils"
import ConnectSocial from "@/shadcn/components/AddSocialModal/connect-social"
import {
  Dialog,
  DialogContent,
  DialogNonModalOverlay,
  DialogPortal,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"

/**
 * The production host for `connect-social.tsx`: it puts the kit's card inside a
 * Radix Dialog and does nothing else. Every other prop passes straight through,
 * so the screens, the copy and the handlers stay documented in one place — the
 * kit itself.
 *
 * Open is not a state here. The legacy modal stack (`App.jsx`, `MODAL_ADD` /
 * `MODAL_CLOSE_ALL`) mounts a modal to show it and unmounts it to close it, so
 * mounted IS open. One consequence: the closing animation never plays, because
 * the node is gone before it could run.
 *
 * `onClose` is the one prop this adds, and it fires for all three dismissals —
 * the header's X, Escape, and a click beside the card. It has to land on the
 * caller's real teardown, not just hide the dialog.
 */

type ConnectSocialDialogProps = React.ComponentProps<typeof ConnectSocial> & {
  onClose: () => void
  /**
   * Radix's modal behaviour, OFF by default here.
   *
   * `modal` puts `pointer-events: none` on <body>, which kills any legacy modal
   * rendered outside this dialog's portal — and these flows raise several
   * through the app-level stack: the upgrade modal, the duplicate-social and
   * blacklist confirms, the Nextdoor warning. They'd be visible (`.dlvr-modal`
   * is z-index 1001 against Radix's 50) and dead.
   *
   * What that costs is replaced by hand: Radix's scroll lock by the same
   * `body.modal-open` class every legacy modal sets, and its overlay — which
   * the primitive renders for modal dialogs only — by `DialogNonModalOverlay`
   * below, so the page behind is dimmed and inert. Keyboard focus is NOT
   * trapped, which matches the legacy modals. Turn `modal` on for a dialog that
   * raises nothing legacy.
   */
  modal?: boolean
  /**
   * Classes for the dialog's own box, not the card — `className` is the kit's
   * and lands on the card, which is what a caller reaching for it means.
   */
  contentClassName?: string
}

export default function ConnectSocialDialog({
  onClose,
  modal = false,
  contentClassName,
  ...props
}: ConnectSocialDialogProps) {
  /*
   * The scroll lock, since `modal={false}` declines Radix's. Same class the
   * legacy modals set, so a legacy modal opening over this one is idempotent
   * rather than fighting it — and `modal-open` is what the stylesheet already
   * hangs `overflow: hidden` on.
   */
  React.useEffect(() => {
    if (modal) return

    document.body.classList.add("modal-open")

    return () => {
      document.body.classList.remove("modal-open")
    }
  }, [modal])

  // Back dismisses the dialog, as it does for every legacy modal.
  useBackToClose(onClose)

  /*
   * Put focus back in the dialog when the screen advances.
   *
   * Advancing unmounts the control that was clicked, and with no focus trap the
   * browser drops focus to <body> — leaving a keyboard or screen-reader user at
   * the top of the document while a dialog is open. Focusing the dialog itself
   * rather than a control inside it puts them at the start of the new screen
   * without this file having to guess which control matters.
   *
   * Skipped when focus already landed somewhere real, so it can't steal focus
   * from a field the user is typing in — and on mount, where Radix's own
   * FocusScope has already placed it.
   */
  const contentRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const active = document.activeElement

    if (active && active !== document.body) return

    contentRef.current?.focus()
  }, [props.screen])

  return (
    <Dialog
      open
      modal={modal}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      {/*
       * Radix omits its overlay entirely for a non-modal dialog, which left the
       * whole page behind this one dimly lit and fully live — hover states,
       * buttons, the lot. This puts the scrim back, and it is also what closes
       * the dialog on a click beside the card, since Radix's own outside
       * detection is turned off below.
       *
       * The content above is `pointer-events-none` with the card
       * `pointer-events-auto`, so a click that isn't on the card falls through
       * to this scrim and lands on its onClick.
       */}
      {modal ? null : (
        <DialogPortal>
          <DialogNonModalOverlay onClick={onClose} />
        </DialogPortal>
      )}
      {/*
       * DialogContent carries its own surface — bg-popover, p-4, rounded-xl, a
       * ring, sm:max-w-sm — and the kit's card IS the surface here, so all of it
       * is switched off rather than painted twice. What remains is what the
       * primitive is actually wanted for: the portal (which already wraps its
       * children in `.tw-root`), the overlay, focus containment and the dismiss
       * handlers.
       *
       * The per-screen card widths stay in the kit, which keeps them in one
       * table — so the cap here is released at `sm` and the card centres itself
       * inside a full-width box. That box would then swallow a click beside the
       * card, which is what `pointer-events-none` here and `pointer-events-auto`
       * on the card below are for: the click falls through to the scrim, whose
       * onClick is what closes the dialog.
       *
       * `showCloseButton={false}` because the kit's header draws its own X,
       * centred against the title by a matching spacer.
       */}
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        showCloseButton={false}
        aria-describedby={undefined}
        /*
         * Radix's outside-detection is switched OFF, and the scrim above closes
         * the dialog itself.
         *
         * Observed in the browser: with `modal={false}` every click fired
         * `onOpenChange(false)` — clicks on the card included — so the dialog
         * closed instead of advancing, and swallowed the action it was closing
         * over. Radix decides "inside" from an `onPointerDownCapture` on the
         * content node, and through this portal that flag was not reaching the
         * document-level listener that reads it.
         *
         * Rather than keep guessing at that, dismissal is now explicit: the
         * scrim's own onClick closes the dialog, the header's X closes it, and
         * Escape still goes through Radix (`onEscapeKeyDown` is a separate path
         * and is not prevented). Nothing is left to infer from event geometry.
         */
        onInteractOutside={(event) => {
          event.preventDefault()
        }}
        className={cn(
          "tw:pointer-events-none tw:gap-0 tw:rounded-none tw:bg-transparent tw:p-0 tw:ring-0 tw:shadow-none tw:sm:max-w-none",
          contentClassName
        )}
      >
        {/*
         * Radix warns without a Title, and a screen reader announces it as the
         * dialog's name. The kit already prints the same string as its visible
         * <h2>, so this copy is hidden rather than drawn. The description is
         * opted out of above instead of hidden here, because the kit's subtitle
         * is absent on some screens.
         */}
        <DialogTitle className="tw:sr-only">
          {props.title ?? "Connect social"}
        </DialogTitle>
        <ConnectSocial
          {...props}
          onClose={onClose}
          className={cn("tw:pointer-events-auto", props.className)}
        />
      </DialogContent>
    </Dialog>
  )
}
